import { Component, inject } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

// Define interfaces for type safety
interface ApiError {
  error?: {
    message?: string;
  };
  message?: string;
}

interface ApiResponse {
  message?: string;
}

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, ReactiveFormsModule, RouterModule],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
})
export class ForgotPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);

  readonly currentYear = new Date().getFullYear();
  readonly forgotPasswordForm: FormGroup<{ email: FormControl<string | null> }>;
  loading = false;
  emailSent = false;
  redirectUrl: string | null = null;

  constructor() {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    // Capture redirect URL from query parameters
    this.route.queryParamMap.subscribe((params) => {
      this.redirectUrl = params.get('redirect-url');
    });
  }

  onSubmit(): void {
    if (
      this.forgotPasswordForm.valid &&
      this.forgotPasswordForm.value.email !== null
    ) {
      this.loading = true;
      const email = this.forgotPasswordForm.value.email as string;

      // Pass redirect URL to the service
      this.authService
        .forgotPassword(
          email,
          this.redirectUrl !== null && this.redirectUrl.trim() !== ''
            ? this.redirectUrl
            : undefined,
        )
        .subscribe({
          next: (data: ApiResponse) => {
            this.loading = false;
            this.emailSent = true;
            this.toastService.showSuccess(
              data.message ?? 'Password reset email sent successfully!',
            );
          },
          error: (err: ApiError) => {
            this.loading = false;
            let errorMessage = 'Failed to send reset email. Please try again.';

            if (err.error?.message) {
              errorMessage = err.error.message;
            } else if (err.message) {
              errorMessage = err.message;
            }

            this.toastService.showError(errorMessage);
          },
        });
    } else {
      this.forgotPasswordForm.markAllAsTouched();
    }
  }

  goBackToLogin(): void {
    if (this.redirectUrl !== null && this.redirectUrl.trim() !== '') {
      void this.router.navigate(['/'], {
        queryParams: { 'redirect-url': this.redirectUrl },
      });
    } else {
      void this.router.navigate(['/']);
    }
  }

  resendEmail(): void {
    this.emailSent = false;
    this.onSubmit();
  }
}
